import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import AppShell from "@/components/AppShell";

/**
 * Companion picker — explicit, free choice. Learned preferences act as a
 * SOFT default (the preferred companion is marked with a small "you usually
 * choose them" badge), but every companion is one tap away. Nothing is
 * locked in.
 */
export default function CompanionPicker() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [continuity, setContinuity] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [chars, cont] = await Promise.all([
          api.get("/catalog/characters"),
          api.get("/continuity"),
        ]);
        setCharacters(chars.data);
        setContinuity(cont.data);
      } catch (e) {
        toast.error("Couldn't load companions.");
      }
    })();
  }, []);

  const isPremium = !!user?.is_premium;
  // Iter 123 — both companions are free; no character premium gate.
  const FREE_COMPANIONS = ["elizabeth", "seriny"];

  const choose = async (characterId) => {
    if (saving) return;
    if (!isPremium && !FREE_COMPANIONS.includes(characterId)) {
      const name = characters.find((c) => c.id === characterId)?.name || "This companion";
      toast(`${name} is a Premium companion. Elizabeth is here for you on the Free plan.`);
      return;
    }
    if (user?.character_id === characterId) {
      nav("/app");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/profile/companion", { character_id: characterId });
      await refresh();
      const name = characters.find((c) => c.id === characterId)?.name || "them";
      toast.success(`You'll spend tonight with ${name}.`);
      nav("/app");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Could not switch.");
    } finally {
      setSaving(false);
    }
  };

  const preferredId = continuity?.learned?.preferred_character_id;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-6 sm:px-8 pt-2 pb-14">
        <Link
          to="/app"
          data-testid="companion-picker-back"
          className="inline-flex items-center gap-2 text-sm text-violet-300/60 hover:text-violet-50 mb-10 transition-soft"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-violet-300/85 mb-3">
          <Sparkles className="w-3 h-3" /> Who do you want to be with tonight?
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-3 leading-tight">
          You can change your mind, any time.
        </h1>
        <p className="text-violet-200/70 mb-10 leading-relaxed max-w-xl">
          Each companion remembers your story separately — switching to someone else does not erase the time you have spent with anyone. You can come back to either side of the door whenever you want.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="companion-picker-grid">
          {characters.map((c, idx) => {
            const isCurrent = user?.character_id === c.id;
            const isPreferred = preferredId === c.id;
            return (
              <motion.button
                key={c.id}
                type="button"
                onClick={() => choose(c.id)}
                disabled={saving}
                data-testid={`companion-pick-${c.id}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.08 + idx * 0.12, ease: [0.22, 0.61, 0.36, 1] }}
                whileHover={{ y: -3 }}
                className={`group text-left rounded-2xl overflow-hidden border transition-soft relative ${
                  isCurrent
                    ? "border-violet-300/65 bg-violet-300/10 glow-violet"
                    : "border-violet-300/20 hover:border-violet-300/50 glass"
                }`}
              >
                <div
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url('${c.image}')` }}
                />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif-display text-2xl" data-testid={`companion-pick-${c.id}-name`}>
                      {c.name}
                    </h3>
                    {isCurrent && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-violet-100">
                        <Check className="w-3 h-3" /> With now
                      </span>
                    )}
                    {!isPremium && !FREE_COMPANIONS.includes(c.id) && (
                      <span
                        className="text-[9px] uppercase tracking-[0.24em] text-violet-100/80 border border-violet-300/30 rounded-full px-2.5 py-1"
                        data-testid={`companion-pick-${c.id}-premium-chip`}
                      >
                        Premium
                      </span>
                    )}
                  </div>
                  <p className="text-xs uppercase tracking-[0.22em] text-violet-300/70 mb-2">
                    {c.archetype}
                  </p>
                  <p className="text-violet-100/85 text-sm leading-relaxed">{c.tagline}</p>
                  {isPreferred && !isCurrent && (
                    <p
                      data-testid={`companion-pick-${c.id}-preferred-hint`}
                      className="mt-3 text-[11px] text-violet-300/65 italic"
                    >
                      You often choose them — but only if you want to.
                    </p>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
